import React, { useEffect, useMemo, useState } from 'react';
import styles from './MissingAssetsBadge.module.scss';
import {
  MISSING_ASSET_EVENT,
  MISSING_ASSET_CLEAR_EVENT,
  MissingAssetDetail,
  getMissingAssetsSnapshot,
} from '../../services/MissingAssets/missingAssetsBus';

type Entry = MissingAssetDetail;

const entryKey = (e: Entry): string =>
  e.kind === 'translation'
    ? `translation:${e.assetName}`
    : `${e.kind}:${e.assetName}:${e.productId}:${e.geometryName}`;

const isDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return new URLSearchParams(window.location.search).get('debug') === '1';
};

const MissingAssetsBadge: React.FC = () => {
  const debug = useMemo(isDebugEnabled, []);
  const [entries, setEntries] = useState<Map<string, Entry>>(new Map());
  const [expanded, setExpanded] = useState<boolean>(true);

  useEffect(() => {
    if (!debug) {
      return;
    }
    // pull anything that was already buffered before this component mounted (e.g. translation
    // warnings fired during initial domain load, before the first React render)
    const initial = getMissingAssetsSnapshot();
    if (initial.length > 0) {
      setEntries((prev) => {
        const next = new Map(prev);
        for (const detail of initial) {
          next.set(entryKey(detail), detail);
        }
        return next;
      });
    }
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<MissingAssetDetail>).detail;
      if (!detail) {
        return;
      }
      setEntries((prev) => {
        const key = entryKey(detail);
        if (prev.has(key)) {
          return prev;
        }
        const next = new Map(prev);
        next.set(key, detail);
        return next;
      });
    };
    const clearHandler = () => setEntries(new Map());
    window.addEventListener(MISSING_ASSET_EVENT, handler);
    window.addEventListener(MISSING_ASSET_CLEAR_EVENT, clearHandler);
    return () => {
      window.removeEventListener(MISSING_ASSET_EVENT, handler);
      window.removeEventListener(MISSING_ASSET_CLEAR_EVENT, clearHandler);
    };
  }, [debug]);

  useEffect(() => {
    if (!debug || entries.size === 0) {
      return;
    }
    const rows = Array.from(entries.values()).map((e) => ({
      kind: e.kind,
      assetName: e.assetName,
      geometry: e.geometryName,
      product: e.productId,
    }));
    // eslint-disable-next-line no-console
    console.groupCollapsed(`[missing-assets] ${rows.length} entries`);
    // eslint-disable-next-line no-console
    console.table(rows);
    // eslint-disable-next-line no-console
    console.groupEnd();
  }, [debug, entries]);

  if (!debug || entries.size === 0) {
    return null;
  }

  const list = Array.from(entries.values());

  return (
    <div className={styles.missingAssetsBadge} role="status" aria-live="polite">
      <div className={styles.header}>
        <span>⚠ Missing assets ({list.length})</span>
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? '–' : '+'}
        </button>
      </div>
      {expanded && (
        <ul className={styles.list}>
          {list.map((e) => (
            <li key={entryKey(e)}>
              <span className={styles.kind}>{e.kind}</span>
              <code>{e.assetName}</code>
              {e.kind !== 'translation' && (
                <> on <code>{e.geometryName}</code></>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MissingAssetsBadge;
